import { useRef, useState } from 'react';
import {
  EXPRESSION_SAMPLES,
  EXPRESSION_VARIABLES,
  MAX_EXPRESSION_LENGTH,
  parseExpression,
  sampleContext,
  type ExpressionVariable,
} from './expression';
import { EXPRESSION_PRESETS } from './presets';
import { IssueList, Labelled, NumberInput, Worked } from './controls';
import { issuesFor, type DefinitionValidation } from './validate';
import { EXPRESSION_FUNCTION_HELP, EXPRESSION_VARIABLE_DETAILS, trimNumber } from './wording';

/**
 * §9.4 — the advanced path: a finish becomes points through a formula rather than a lookup table.
 *
 * <b>An expression is the one scoring mode an operator can get wrong invisibly.</b> A placement
 * table is readable at a glance and monotone by construction; a formula is neither, and the
 * failure it produces is not an error message but a season of quietly wrong standings. So the
 * whole design of this editor is about making it judgeable before it is published:
 *
 * - **The evaluation table is the point, not the input box.** Four sample finishes, scored live as
 *   the operator types. Two of them differ only in field size and two only in finish, so a formula
 *   that ignores either shows up as two identical rows without anybody having to reason about it.
 * - **The palette is the contract.** Nine variables and thirteen functions, and nothing else
 *   parses — so they are listed rather than left to be discovered by rejection. Clicking a chip
 *   inserts it at the cursor, and a chip the formula already reads is marked.
 * - **The error carries a position**, because "unknown identifier" with nothing pointing at which
 *   one is not something an operator can act on.
 *
 * Everything here runs against `expression.ts`, the client mirror of the server's parser. It is
 * feedback: `PointsExpression.Parse` still decides on write, and the preview pane's standings still
 * come from the engine. What the mirror buys is that an invalid formula never leaves the screen —
 * §9.4's last requirement, and the one `validateDefinition` enforces by blocking the save.
 */
export function ExpressionEditor({
  value,
  onChange,
  validation,
  readOnly = false,
}: {
  value: string;
  onChange: (next: string) => void;
  validation: DefinitionValidation;
  readOnly?: boolean;
}) {
  const input = useRef<HTMLTextAreaElement>(null);
  const [buyIn, setBuyIn] = useState<number | null>(400);

  const parsed = parseExpression(value);
  const used = new Set<ExpressionVariable>(parsed.ok ? parsed.variables : []);

  /**
   * Inserts at the caret rather than appending. Somebody fixing the middle of a formula and
   * reaching for a variable expects it where they are looking, and an append would send them back
   * to cut and paste it into place.
   */
  const insert = (text: string) => {
    const box = input.current;
    const start = box?.selectionStart ?? value.length;
    const end = box?.selectionEnd ?? value.length;

    onChange(value.slice(0, start) + text + value.slice(end));

    // After React has re-rendered with the new value, or the caret lands at the old offset.
    requestAnimationFrame(() => {
      box?.focus();
      box?.setSelectionRange(start + text.length, start + text.length);
    });
  };

  return (
    <div className="space-y-4">
      {!readOnly ? (
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-800/45">
            Start from
          </div>
          <div className="flex flex-wrap gap-2">
            {EXPRESSION_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => onChange(preset.expression)}
                className="max-w-[17rem] border border-line bg-surface p-2.5 text-left transition hover:border-felt-700"
              >
                <span className="block text-[12px] font-semibold text-ink-950">{preset.label}</span>
                <span className="mt-0.5 block text-[10.5px] leading-snug text-ink-800/55">
                  {preset.description}
                </span>
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-ink-800/45">
            Choosing one replaces what is in the box. Nothing is saved until you save.
          </p>
        </div>
      ) : null}

      <div>
        <textarea
          ref={input}
          value={value}
          disabled={readOnly}
          rows={3}
          spellCheck={false}
          maxLength={MAX_EXPRESSION_LENGTH}
          aria-label="Scoring formula"
          aria-invalid={!parsed.ok || undefined}
          onChange={(e) => onChange(e.target.value)}
          placeholder="ln(fieldSize / effectivePlace)"
          className={`w-full resize-y border bg-surface px-2.5 py-2 font-mono text-[12.5px] leading-relaxed text-ink-950 outline-none focus:border-felt-500 disabled:bg-ground disabled:text-ink-800/50 ${
            parsed.ok ? 'border-line' : 'border-bad'
          }`}
        />

        <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <ExpressionError source={value} parsed={parsed} />
          <span className="num shrink-0 text-[10.5px] text-ink-800/40">
            {value.length} / {MAX_EXPRESSION_LENGTH}
          </span>
        </div>

        <IssueList className="mt-1" issues={issuesFor(validation, 'scoring')} />
      </div>

      {!readOnly ? (
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-800/45">
            Values you can use
          </div>
          <div className="flex flex-wrap gap-1.5">
            {EXPRESSION_VARIABLES.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => insert(name)}
                title={EXPRESSION_VARIABLE_DETAILS[name]}
                className={`border px-2 py-1 font-mono text-[11px] transition ${
                  used.has(name)
                    ? 'border-felt-700 bg-felt-50/60 text-ink-950'
                    : 'border-line bg-surface text-ink-800/70 hover:border-ink-800/40 hover:text-ink-950'
                }`}
              >
                {name}
              </button>
            ))}
          </div>

          <dl className="mt-2 space-y-1">
            {EXPRESSION_VARIABLES.map((name) => (
              <div key={name} className="flex flex-wrap gap-x-2 text-[11px] leading-snug">
                <dt className="num shrink-0 font-mono text-ink-800/60">{name}</dt>
                <dd className="min-w-0 flex-1 text-ink-800/50">
                  {EXPRESSION_VARIABLE_DETAILS[name]}
                </dd>
              </div>
            ))}
          </dl>

          <div className="mt-3 border-t border-line pt-2.5">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-800/45">
              And these functions
            </div>
            <dl className="space-y-1">
              {EXPRESSION_FUNCTION_HELP.map((entry) => (
                <div key={entry.label} className="flex flex-wrap gap-x-2 text-[11px] leading-snug">
                  <dt className="shrink-0 font-mono text-ink-800/60">{entry.label}</dt>
                  <dd className="min-w-0 flex-1 text-ink-800/50">{entry.detail}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-1.5 text-[11px] text-ink-800/45">
              Nothing else parses — no conditionals, and no way to name anything not on these two
              lists.
            </p>
          </div>
        </div>
      ) : null}

      <SampleTable parsed={parsed} buyIn={buyIn} onBuyInChange={setBuyIn} />
    </div>
  );
}

/**
 * §9.4's inline error, with the character position.
 *
 * The position is shown as the text either side of it rather than as a number: "position 23" makes
 * an operator count characters, while the offending fragment with a caret under it is the thing
 * they can look at in the box above.
 */
function ExpressionError({
  source,
  parsed,
}: {
  source: string;
  parsed: ReturnType<typeof parseExpression>;
}) {
  if (parsed.ok) {
    return (
      <span className="text-[11.5px] text-ink-800/45">
        Reads as a formula. The table below is what it does.
      </span>
    );
  }

  const { message, position } = parsed.error;

  if (position === null || position > source.length) {
    return <span className="min-w-0 text-[11.5px] text-bad">{message}</span>;
  }

  const from = Math.max(0, position - 18);
  const to = Math.min(source.length, position + 18);

  // A formula may span lines — whitespace is whitespace to the parser — so the excerpt flattens
  // them. `replace` is length-preserving, which is what keeps the caret under the right character.
  const excerpt = source.slice(from, to).replace(/\s/g, ' ');

  return (
    <span className="min-w-0">
      <span className="block text-[11.5px] text-bad">{message}</span>
      <span className="mt-0.5 block whitespace-pre font-mono text-[11px] leading-tight text-ink-800/55">
        {(from > 0 ? '…' : '') + excerpt + (to < source.length ? '…' : '')}
        {'\n'}
        {' '.repeat((from > 0 ? 1 : 0) + (position - from))}
        <span className="text-bad">▲</span>
      </span>
    </span>
  );
}

/**
 * §9.4's live evaluation table — "this turns an abstract expression into something judgeable".
 *
 * The buy-in is a control because it is the one input §9.4's four samples leave fixed, and a
 * formula weighting by buy-in is invisible without it. Everything else the samples model is stated
 * under the table rather than hidden — and only the parts this formula actually reads, so a
 * formula ignoring `prizeWon` is not told what its sample prize would have been.
 */
function SampleTable({
  parsed,
  buyIn,
  onBuyInChange,
}: {
  parsed: ReturnType<typeof parseExpression>;
  buyIn: number | null;
  onBuyInChange: (next: number | null) => void;
}) {
  const effectiveBuyIn = buyIn ?? 0;

  const rows = EXPRESSION_SAMPLES.map((sample) => {
    const context = sampleContext(sample, effectiveBuyIn);
    return { sample, context, result: parsed.ok ? parsed.evaluate(context) : null };
  });

  const first = rows[0];
  const flat =
    parsed.ok &&
    rows.every((row) => row.result!.value === first!.result!.value);

  return (
    <div className="border-t border-line pt-4">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-800/45">
            What it scores
          </div>
          <p className="mt-0.5 text-[11px] text-ink-800/50">
            Four sample finishes, scored as you type. Before any weighting or participation points.
          </p>
        </div>
        <Labelled label="At a buy-in of" className="w-28 shrink-0">
          <NumberInput
            value={buyIn}
            onChange={onBuyInChange}
            ariaLabel="Sample buy-in"
            min={0}
            step={100}
            className="w-full"
          />
        </Labelled>
      </div>

      <table className="w-full text-left text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.08em] text-ink-800/45">
            <th className="pb-1.5 font-semibold">Finish</th>
            <th className="pb-1.5 text-right font-semibold">Points</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ sample, result }) => (
            <tr key={sample.label} className="border-t border-line/60">
              <td className="py-1.5 text-[12.5px] text-ink-950">{sample.label}</td>
              <td className="num py-1.5 text-right text-[12.5px] text-ink-950">
                {result === null ? (
                  <span className="text-ink-800/35">—</span>
                ) : (
                  <>
                    {trimNumber(Math.round(result.value * 100) / 100)}
                    {result.warning ? <span className="ml-1 text-warn">!</span> : null}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {parsed.ok ? (
        <div className="mt-2 space-y-1.5">
          {rows
            .filter((row) => row.result!.warning)
            .slice(0, 1)
            .map((row) => (
              // §9.7: "show the failing sample input, not just a parser message".
              <p key={row.sample.label} className="text-[11.5px] text-warn">
                {row.sample.label}: {row.result!.warning}
              </p>
            ))}

          {flat ? (
            <Worked>
              Every sample scores the same, so this formula does not depend on where somebody
              finished or how big the field was. That is a valid system — everyone who cashes earns
              alike — but it is rarely what was meant.
            </Worked>
          ) : null}

          <Worked>{samplesNote(parsed.variables, effectiveBuyIn)}</Worked>
        </div>
      ) : (
        <p className="mt-2 text-[11.5px] text-ink-800/45">
          Fix the formula above and these fill in.
        </p>
      )}
    </div>
  );
}

/**
 * What the samples assume, naming **only the variables this formula reads**.
 *
 * The four samples fix a finish and a field; the other seven variables have to be modelled, and
 * saying so is the difference between a worked example and a number this screen invented. Nothing
 * is listed that the formula does not use, so the sentence stays about the operator's own work.
 */
function samplesNote(variables: readonly ExpressionVariable[], buyIn: number): string {
  const used = new Set(variables);
  const parts: string[] = [];

  if (used.has('place') || used.has('effectivePlace')) {
    parts.push('none of them ended in a chop, so the published place and the effective place agree');
  }
  if (used.has('uniquePlayers')) parts.push('nobody re-entered, so players and entries are the same number');
  if (used.has('cashers')) parts.push('15% of the field was paid');
  if (used.has('prizePool')) parts.push('the pool is buy-in × entries, with no rake taken out');
  if (used.has('prizeWon')) {
    parts.push('the prize is an illustrative share of that pool, not any room’s payout schedule');
  }
  if (used.has('isDeal')) parts.push('isDeal is 0 in every sample');

  const buyInNote = used.has('buyIn') || used.has('prizePool')
    ? `Every sample is a $${buyIn.toLocaleString('en-US')} event. `
    : 'This formula does not read the buy-in, so changing it above will not move these figures. ';

  return parts.length === 0
    ? `${buyInNote}These are samples, not results from your room.`
    : `${buyInNote}They also assume ${joinWith(parts)}. These are samples, not results from your room.`;
}

function joinWith(parts: string[]): string {
  if (parts.length === 1) return parts[0]!;
  return `${parts.slice(0, -1).join('; ')}; and ${parts[parts.length - 1]}`;
}
